import React, { useState } from 'react';
import { useMode } from '../contexts/ModeContext';
import { useLang } from '../contexts/LangContext';
import { useNavigate } from 'react-router-dom';
import { getTranslation } from '../translations';
import { authAPI, setAuthToken } from '../utils/api';
import { toast } from 'sonner';
import { BookOpen, Sparkles, ArrowRight, Mail, Lock, User as UserIcon, Calendar, ChevronLeft } from 'lucide-react';

const Auth = () => {
  const { isKidsMode, toggleMode } = useMode();
  const { lang, toggleLang } = useLang();
  const navigate = useNavigate();
  const t = (key) => getTranslation(lang, key);

  const accent = isKidsMode ? 'amber' : 'teal';
  const accentSolid = isKidsMode ? 'bg-amber-500 hover:bg-amber-600' : 'bg-teal-700 hover:bg-teal-800';
  const accentRing = isKidsMode ? 'focus:ring-amber-500/40' : 'focus:ring-teal-600/40';
  const accentText = isKidsMode ? 'text-amber-700' : 'text-teal-800';

  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('form'); // 'form' | 'verify' | 'forgot' | 'reset' | 'pending'
  const [verifyCode, setVerifyCode] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resetCode, setResetCode] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [parentConsent, setParentConsent] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    age_group: '10+',
    preferred_language: lang,
  });

  // Resend cooldown ticker
  React.useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCooldown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let response;
      if (isLogin) {
        response = await authAPI.login({ email: formData.email, password: formData.password });
        setAuthToken(response.data.access_token);
        toast.success(lang === 'tr' ? 'Giriş başarılı!' : 'Login successful!');
        navigate('/dashboard');
      } else {
        if (!legalAccepted) {
          toast.error(lang === 'tr'
            ? 'Devam etmek için Kullanım Koşulları ve Gizlilik Politikası\'nı kabul etmelisiniz.'
            : 'You must accept the Terms and Privacy Policy to continue.');
          setLoading(false);
          return;
        }
        response = await authAPI.register(formData);
        // If admin approval required, show pending state and DO NOT auto-login
        if (response.data?.requires_approval) {
          setStep('pending');
          toast.success(lang === 'tr'
            ? 'Kayıt başarılı! Hesabınız admin onayı bekliyor.'
            : 'Registration successful! Awaiting admin approval.');
          return;
        }
        setAuthToken(response.data.access_token);
        // Persist consent flags (KVKK + Terms must be true to reach here).
        // Parent/guardian consent is only set if user is signing up in kids context.
        try {
          const { consentsAPI } = await import('../utils/api');
          await consentsAPI.update({
            consent_kvkk: true,
            consent_terms: true,
            is_parent_or_guardian: !!parentConsent,
          });
        } catch (_) { /* non-blocking */ }
        const verified = response.data?.user?.email_verified;
        if (verified) {
          toast.success(lang === 'tr' ? 'Kayıt başarılı!' : 'Registration successful!');
          navigate('/dashboard');
        } else {
          setStep('verify');
          setResendCooldown(60);
          toast.success(lang === 'tr' ? 'Kayıt tamamlandı! E-postanıza gönderilen 6 haneli kodu girin.' : 'Registered! Enter the 6-digit code sent to your email.');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (lang === 'tr' ? 'Bir hata oluştu' : 'An error occurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(verifyCode)) {
      toast.error(lang === 'tr' ? 'Lütfen 6 haneli kodu girin' : 'Enter the 6-digit code');
      return;
    }
    setLoading(true);
    try {
      const res = await authAPI.verifyEmail(formData.email, verifyCode);
      if (res?.data?.access_token) setAuthToken(res.data.access_token);
      toast.success(lang === 'tr' ? 'E-postanız doğrulandı!' : 'Email verified!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || (lang === 'tr' ? 'Kod doğrulanamadı' : 'Code invalid'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    try {
      await authAPI.resendVerification(formData.email);
      toast.success(lang === 'tr' ? 'Yeni kod e-postanıza gönderildi' : 'New code sent');
      setResendCooldown(60);
    } catch (error) {
      const detail = error.response?.data?.detail || (lang === 'tr' ? 'Kod tekrar gönderilemedi' : 'Could not resend');
      toast.error(detail);
      setResendCooldown(30);
    }
  };

  const handleSkipVerify = () => {
    toast.info(lang === 'tr' ? 'Doğrulamayı daha sonra yapabilirsiniz' : 'You can verify later');
    navigate('/dashboard');
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      toast.error(lang === 'tr' ? 'E-posta adresinizi girin' : 'Enter your email');
      return;
    }
    setLoading(true);
    try {
      await authAPI.forgotPassword(formData.email);
      toast.success(lang === 'tr' ? `Sıfırlama kodu gönderildi: ${formData.email}` : `Reset code sent: ${formData.email}`);
      setStep('reset');
      setResendCooldown(60);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === 'tr' ? 'Bir hata oluştu' : 'An error occurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(resetCode)) {
      toast.error(lang === 'tr' ? '6 haneli kodu girin' : 'Enter the 6-digit code');
      return;
    }
    if (resetNewPassword.length < 6) {
      toast.error(lang === 'tr' ? 'Yeni şifre en az 6 karakter olmalı' : 'Password must be at least 6 chars');
      return;
    }
    setLoading(true);
    try {
      const res = await authAPI.resetPassword(formData.email, resetCode, resetNewPassword);
      if (res?.data?.access_token) setAuthToken(res.data.access_token);
      toast.success(lang === 'tr' ? 'Şifreniz güncellendi! Giriş yapıldı.' : 'Password updated! Logged in.');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === 'tr' ? 'Kod doğrulanamadı' : 'Code invalid'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendReset = async () => {
    if (resendCooldown > 0) return;
    try {
      await authAPI.forgotPassword(formData.email);
      toast.success(lang === 'tr' ? 'Yeni kod gönderildi' : 'New code sent');
      setResendCooldown(60);
    } catch {
      setResendCooldown(30);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const titleByStep = () => {
    if (step === 'verify') return lang === 'tr' ? 'E-postanı Doğrula' : 'Verify Email';
    if (step === 'forgot') return lang === 'tr' ? 'Şifremi Unuttum' : 'Forgot Password';
    if (step === 'reset') return lang === 'tr' ? 'Yeni Şifre Belirle' : 'Set New Password';
    if (step === 'pending') return lang === 'tr' ? 'Onay Bekleniyor' : 'Awaiting Approval';
    return isLogin ? (lang === 'tr' ? 'Tekrar Hoş Geldin' : 'Welcome Back') : (lang === 'tr' ? 'Aramıza Katıl' : 'Join Us');
  };

  const subtitleByStep = () => {
    if (step === 'verify') return lang === 'tr' ? `${formData.email} adresine gönderilen 6 haneli kodu gir` : `Enter the 6-digit code sent to ${formData.email}`;
    if (step === 'forgot') return lang === 'tr' ? 'E-posta adresine sıfırlama kodu göndereceğiz' : 'We\'ll send a reset code to your email';
    if (step === 'reset') return lang === 'tr' ? `${formData.email} adresine gönderilen kodu ve yeni şifreni gir` : `Enter code sent to ${formData.email} and a new password`;
    if (step === 'pending') return lang === 'tr' ? 'Hesabınız onaylandığında size e-posta ile bildirim göndereceğiz.' : 'You will receive an email once your account is approved.';
    return isLogin
      ? (lang === 'tr' ? 'Hafızam yolculuğuna devam et' : 'Continue your Hafızım journey')
      : (lang === 'tr' ? 'Kur\'an-ı Kerim\'i öğrenmeye başla' : 'Begin learning the Holy Quran');
  };

  const inputCls = `w-full px-4 py-3 rounded-2xl border border-stone-200 bg-white text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 ${accentRing} focus:border-transparent transition-all`;

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        background: isKidsMode
          ? 'radial-gradient(circle at 20% 0%, #fef3c7 0%, transparent 40%), radial-gradient(circle at 80% 100%, #fed7aa 0%, transparent 40%), #fafaf9'
          : 'radial-gradient(circle at 20% 0%, #ccfbf1 0%, transparent 40%), radial-gradient(circle at 80% 100%, #99f6e4 0%, transparent 40%), #fafaf9',
      }}
      data-testid="auth-screen"
    >
      {/* Top bar */}
      <header className="flex items-center justify-between px-5 sm:px-8 py-4">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 text-stone-700 hover:text-stone-900 text-sm font-medium"
          data-testid="back-to-home-button"
        >
          <ChevronLeft className="w-4 h-4" />
          {lang === 'tr' ? 'Ana sayfa' : 'Home'}
        </button>
        <div className="flex items-center gap-2">
          {/* Language selector hidden — Turkish only */}
          <button
            type="button"
            onClick={toggleMode}
            data-testid="auth-mode-toggle"
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${isKidsMode ? 'bg-amber-100 border-amber-300 text-amber-800' : 'bg-teal-100 border-teal-300 text-teal-800'}`}
          >
            {isKidsMode ? '🧒 ' + (lang === 'tr' ? 'Çocuk' : 'Kids') : '👤 ' + (lang === 'tr' ? 'Yetişkin' : 'Adult')}
          </button>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-5 pb-12">
        <div className="w-full max-w-md">
          {/* Brand */}
          <div className="text-center mb-8">
            <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4 ${isKidsMode ? 'bg-amber-500' : 'bg-teal-700'} shadow-lg`}>
              <BookOpen className="w-7 h-7 text-white" />
            </div>
            <h1 className={`text-3xl font-bold text-stone-900 tracking-tight`} style={{ fontFamily: 'Outfit, sans-serif' }}>
              Hafızım
            </h1>
            <p className="text-sm text-stone-500 mt-1">
              {lang === 'tr' ? 'Kur\'an Öğrenme Yolculuğu' : 'Quran Learning Journey'}
            </p>
          </div>

          {/* Card */}
          <div className="bg-white/85 backdrop-blur-md rounded-3xl border border-stone-200 shadow-xl p-7 sm:p-8" data-testid="auth-form">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {titleByStep()}
              </h2>
              <p className="text-sm text-stone-500 mt-1">{subtitleByStep()}</p>
            </div>

            {step === 'pending' ? (
              <div className="space-y-4" data-testid="pending-approval-screen">
                <div className={`p-6 rounded-2xl text-center ${isKidsMode ? 'bg-amber-50 border border-amber-200' : 'bg-teal-50 border border-teal-200'}`}>
                  <div className={`inline-flex items-center justify-center w-14 h-14 rounded-full mb-3 ${isKidsMode ? 'bg-amber-100 text-amber-700' : 'bg-teal-100 text-teal-700'}`}>
                    <Sparkles className="w-7 h-7" />
                  </div>
                  <p className="text-stone-800 text-sm leading-relaxed">
                    {lang === 'tr'
                      ? <>Kayıt için teşekkürler, <strong>{formData.name || 'değerli kullanıcı'}</strong>.<br/>Hesabınız <strong>admin onayı</strong> bekliyor. Onaylandığında e-posta ile bildirim göndereceğiz.</>
                      : <>Thanks for registering, <strong>{formData.name || 'friend'}</strong>.<br/>Your account is awaiting <strong>admin approval</strong>. We will email you once approved.</>}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => { setStep('form'); setIsLogin(true); }}
                  data-testid="pending-back-to-login"
                  className={`w-full py-3 rounded-2xl font-semibold text-white text-sm ${accentSolid}`}
                >
                  {lang === 'tr' ? 'Anlaşıldı' : 'Got it'}
                </button>
              </div>
            ) : step === 'forgot' ? (
              <form onSubmit={handleForgotSubmit} className="space-y-4" data-testid="forgot-form">
                <Field icon={Mail} type="email" name="email" value={formData.email} onChange={handleChange} placeholder="ornek@email.com" autoFocus inputCls={inputCls} testId="forgot-email-input" />
                <SubmitBtn loading={loading} accentSolid={accentSolid} testId="forgot-submit-button">
                  {lang === 'tr' ? 'Kod Gönder' : 'Send Code'}
                </SubmitBtn>
                <TextLink onClick={() => setStep('form')} testId="forgot-back-button">
                  ← {lang === 'tr' ? 'Girişe Dön' : 'Back to Login'}
                </TextLink>
              </form>
            ) : step === 'reset' ? (
              <form onSubmit={handleResetSubmit} className="space-y-4" data-testid="reset-form">
                <CodeInput value={resetCode} onChange={setResetCode} placeholder="000000" accentRing={accentRing} testId="reset-code-input" />
                <Field icon={Lock} type="password" value={resetNewPassword} onChange={(e) => setResetNewPassword(e.target.value)} placeholder={lang === 'tr' ? 'Yeni şifre (min 6)' : 'New password (min 6)'} inputCls={inputCls} testId="reset-new-password-input" />
                <SubmitBtn loading={loading} disabled={resetCode.length !== 6 || resetNewPassword.length < 6} accentSolid={accentSolid} testId="reset-submit-button">
                  {lang === 'tr' ? 'Şifremi Güncelle' : 'Update Password'}
                </SubmitBtn>
                <div className="flex items-center justify-between text-sm pt-1">
                  <button type="button" onClick={handleResendReset} disabled={resendCooldown > 0} className={`${accentText} hover:underline disabled:text-stone-400 disabled:no-underline`} data-testid="reset-resend-button">
                    {resendCooldown > 0 ? `${lang === 'tr' ? 'Tekrar gönder' : 'Resend'} (${resendCooldown}s)` : (lang === 'tr' ? 'Kodu tekrar gönder' : 'Resend code')}
                  </button>
                  <TextLink onClick={() => setStep('form')} testId="reset-back-button">
                    ← {lang === 'tr' ? 'Girişe Dön' : 'Back'}
                  </TextLink>
                </div>
              </form>
            ) : step === 'verify' ? (
              <form onSubmit={handleVerify} className="space-y-4" data-testid="verify-form">
                <CodeInput value={verifyCode} onChange={setVerifyCode} placeholder="000000" accentRing={accentRing} testId="verify-code-input" />
                <p className="text-xs text-stone-500 -mt-1">
                  {lang === 'tr' ? 'Kodu bulamıyorsan spam klasörünü kontrol et. 15 dk geçerli.' : 'Check your spam folder. Valid for 15 minutes.'}
                </p>
                <SubmitBtn loading={loading} disabled={verifyCode.length !== 6} accentSolid={accentSolid} testId="verify-submit-button">
                  {lang === 'tr' ? 'Doğrula' : 'Verify'}
                </SubmitBtn>
                <div className="flex items-center justify-between text-sm pt-2">
                  <button type="button" onClick={handleResend} disabled={resendCooldown > 0} className={`${accentText} hover:underline disabled:text-stone-400 disabled:no-underline disabled:cursor-not-allowed`} data-testid="verify-resend-button">
                    {resendCooldown > 0 ? `${lang === 'tr' ? 'Tekrar gönder' : 'Resend'} (${resendCooldown}s)` : (lang === 'tr' ? 'Kodu tekrar gönder' : 'Resend code')}
                  </button>
                  <TextLink onClick={handleSkipVerify} testId="verify-skip-button">
                    {lang === 'tr' ? 'Daha sonra' : 'Later'} →
                  </TextLink>
                </div>
              </form>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {!isLogin && (
                  <Field icon={UserIcon} type="text" name="name" value={formData.name} onChange={handleChange} placeholder={lang === 'tr' ? 'Adın' : 'Your name'} required inputCls={inputCls} testId="name-input" />
                )}
                <Field icon={Mail} type="email" name="email" value={formData.email} onChange={handleChange} placeholder={lang === 'tr' ? 'E-posta' : 'Email'} required inputCls={inputCls} testId="email-input" />
                <Field icon={Lock} type="password" name="password" value={formData.password} onChange={handleChange} placeholder={lang === 'tr' ? 'Şifre' : 'Password'} required inputCls={inputCls} testId="password-input" />

                {!isLogin && (
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
                    <select
                      name="age_group"
                      value={formData.age_group}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 rounded-2xl border border-stone-200 bg-white text-stone-900 focus:outline-none focus:ring-2 ${accentRing}`}
                      data-testid="age-group-select"
                    >
                      <option value="1-5">{lang === 'tr' ? '1-5 Yaş' : 'Ages 1-5'}</option>
                      <option value="5-10">{lang === 'tr' ? '5-10 Yaş' : 'Ages 5-10'}</option>
                      <option value="10+">{lang === 'tr' ? '10+ Yaş' : 'Ages 10+'}</option>
                    </select>
                  </div>
                )}

                {!isLogin && (
                  <label className="flex items-start gap-2 text-xs text-stone-700 cursor-pointer select-none" data-testid="legal-consent-label">
                    <input
                      type="checkbox"
                      checked={legalAccepted}
                      onChange={(e) => setLegalAccepted(e.target.checked)}
                      required
                      className="mt-0.5 w-4 h-4 rounded border-stone-300"
                      data-testid="legal-consent-checkbox"
                    />
                    <span className="leading-snug">
                      {lang === 'tr' ? (
                        <>
                          <a href="/kullanim-kosullari" target="_blank" rel="noopener noreferrer" className={`${accentText} underline font-semibold`}>Kullanım Koşulları</a>
                          {' ve '}
                          <a href="/gizlilik-politikasi" target="_blank" rel="noopener noreferrer" className={`${accentText} underline font-semibold`}>Gizlilik Politikası / KVKK Aydınlatma Metni</a>
                          {'\'ni okudum, kabul ediyorum.'}
                        </>
                      ) : (
                        <>I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" className={`${accentText} underline font-semibold`}>Terms</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer" className={`${accentText} underline font-semibold`}>Privacy Policy</a>.</>
                      )}
                    </span>
                  </label>
                )}

                {!isLogin && (
                  <label className="flex items-start gap-2 text-xs text-stone-700 cursor-pointer select-none" data-testid="parent-consent-label">
                    <input
                      type="checkbox"
                      checked={parentConsent}
                      onChange={(e) => setParentConsent(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-stone-300"
                      data-testid="parent-consent-checkbox"
                    />
                    <span className="leading-snug">
                      {lang === 'tr' ? (
                        <>18 yaşından büyüğüm. Eğer Çocuk Modu&apos;nu çocuğum için kullanacaksam, ebeveyn/yasal vasi olarak bu hesabı onun adına yönetiyorum.</>
                      ) : (
                        <>I am 18+. If using Kids Mode for my child, I confirm I am their parent/legal guardian and manage this account on their behalf.</>
                      )}
                    </span>
                  </label>
                )}

                <SubmitBtn loading={loading} accentSolid={accentSolid} testId="submit-button">
                  <span className="inline-flex items-center justify-center gap-2">
                    {isLogin ? (lang === 'tr' ? 'Giriş Yap' : 'Sign In') : (lang === 'tr' ? 'Hesap Oluştur' : 'Create Account')}
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </SubmitBtn>

                {isLogin && (
                  <div className="text-center pt-1">
                    <button type="button" onClick={() => setStep('forgot')} className={`text-sm ${accentText} hover:underline`} data-testid="forgot-password-link">
                      {lang === 'tr' ? 'Şifremi unuttum' : 'Forgot password?'}
                    </button>
                  </div>
                )}
              </form>
            )}

            {step === 'form' && (
              <>
                <div className="my-5 flex items-center gap-3">
                  <div className="flex-1 h-px bg-stone-200" />
                  <span className="text-xs uppercase tracking-[0.2em] text-stone-400">{lang === 'tr' ? 'veya' : 'or'}</span>
                  <div className="flex-1 h-px bg-stone-200" />
                </div>
                <button
                  type="button"
                  onClick={() => setIsLogin(!isLogin)}
                  className={`w-full py-3 rounded-2xl border-2 font-semibold text-sm transition-all ${isKidsMode ? 'border-amber-300 text-amber-800 hover:bg-amber-50' : 'border-teal-300 text-teal-800 hover:bg-teal-50'}`}
                  data-testid="toggle-auth-button"
                >
                  {isLogin
                    ? (lang === 'tr' ? 'Hesabın yok mu? Kayıt ol' : 'No account? Sign up')
                    : (lang === 'tr' ? 'Hesabın var mı? Giriş yap' : 'Have an account? Sign in')}
                </button>
              </>
            )}
          </div>

          {/* Footer tagline */}
          <p className="text-center text-xs text-stone-400 mt-6 flex items-center justify-center gap-1.5">
            <Sparkles className="w-3 h-3" />
            {lang === 'tr' ? 'Diyanet kaynaklı içerikle' : 'Powered by Diyanet sources'}
          </p>
        </div>
      </div>
    </div>
  );
};

// Reusable subcomponents
const Field = ({ icon: Icon, inputCls, testId, ...rest }) => (
  <div className="relative">
    {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />}
    <input className={`${inputCls} ${Icon ? 'pl-10' : ''}`} data-testid={testId} {...rest} />
  </div>
);

const SubmitBtn = ({ loading, disabled, accentSolid, testId, children }) => (
  <button
    type="submit"
    disabled={loading || disabled}
    className={`w-full py-3 rounded-2xl font-semibold text-white text-sm shadow-md transition-all ${accentSolid} disabled:opacity-50 disabled:cursor-not-allowed`}
    data-testid={testId}
  >
    {loading ? '...' : children}
  </button>
);

const TextLink = ({ onClick, testId, children }) => (
  <button type="button" onClick={onClick} className="text-sm text-stone-500 hover:text-stone-800" data-testid={testId}>
    {children}
  </button>
);

const CodeInput = ({ value, onChange, placeholder, accentRing, testId }) => (
  <input
    type="text"
    inputMode="numeric"
    pattern="\d{6}"
    maxLength={6}
    value={value}
    onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
    placeholder={placeholder}
    autoFocus
    className={`w-full px-4 py-3 rounded-2xl border border-stone-200 bg-white text-center text-2xl tracking-[8px] font-mono text-stone-900 focus:outline-none focus:ring-2 ${accentRing}`}
    data-testid={testId}
  />
);

export default Auth;
